import React from 'react';
import { useRouter } from 'next/router';
import { useTranslation } from 'next-i18next';
import * as Yup from 'yup';
import { useToggle } from 'react-use';
import { useFormik } from 'formik';

import { getErrorMessage } from '@utils/validation';
import { getLinkDataFromUrl, getRouteUrl } from '@utils/urls';
import request from '@utils/request';

import { StaticCard } from 'app/common/components/CardBack/StaticCard';
import FlipCardModal from 'app/common/components/FlipCardModal';
import { REQUIRED_FIELD_ERROR } from '@common/constants/formValidation';
import { MASKED_INPUT_TYPE } from '@common/constants/maskedInput';
import { TextInputMasked } from '@common/components/forms';
import * as endpoints from '@common/endpoints';
import { GIFT_CARDS_BALANCE_CHECK, GIFT_CARDS_VIEW } from '@common/routes';
import { GIFT_CARDS_VIEW_MODE } from '@common/constants/giftCards';
import { GIFT_CARD_BACK, VIP_CARD } from '@common/images';

import {
  Description,
  FormColumn,
  GiftCardsBalanceContent,
  GiftCardsBalanceWrapper,
  HowToButton,
  StyledFormControl,
  SubmitButton,
  Title,
} from './GiftCardBalance.styled';

const blockOptions = {
  0: {
    from: 0,
    mask: global.IMask.MaskedRange,
    maxLength: 1,
    placeholderChar: '0',
    to: 9,
  },
};

export function GiftCardBalance() {
  const router = useRouter();
  const { t } = useTranslation('common');
  const [isModalOpen, setIsModalOpen] = useToggle(false);

  const formik = useFormik({
    initialValues: {
      cardNr: '',
      pinCode: '',
    },
    onSubmit: async ({ cardNr, pinCode }) => {
      try {
        const {
          data: { cardNumber, balance },
        } = await request.post(endpoints.giftCardBalanceCheck(), {
          card_number: cardNr,
          pin: pinCode,
        });

        router.push(
          {
            pathname: GIFT_CARDS_BALANCE_CHECK,
            query: {
              cardNumber,
              balance,
            },
          },
          getRouteUrl(router.locale, GIFT_CARDS_BALANCE_CHECK),
        );
      } catch (error) {
        const redirect = error?.response?.data?.redirect;
        const friendlyError = error?.response?.data?.friendlyError;

        if (redirect) {
          const { urlObject } = getLinkDataFromUrl(redirect) || {};

          if (urlObject) {
            router.push(
              urlObject,
              getRouteUrl(router.locale, urlObject.pathname, urlObject.query),
            );
          }
        }

        if (friendlyError) {
          router.push(
            {
              pathname: GIFT_CARDS_VIEW,
              query: {
                view: GIFT_CARDS_VIEW_MODE[router.locale].BALANCE,
                friendlyError,
              },
            },
            `${getRouteUrl(router.locale, GIFT_CARDS_VIEW, {
              view: GIFT_CARDS_VIEW_MODE[router.locale].BALANCE,
            })}?friendlyError=${friendlyError}`,
          );
        }
      }
    },
    validationSchema: GiftCardBalance.validationSchema,
  });

  const {
    values,
    handleSubmit,
    handleBlur,
    touched,
    errors,
    isSubmitting,
    setFieldValue,
  } = formik;

  return (
    <React.Fragment>
      <FlipCardModal
        isOpen={isModalOpen}
        onClose={() => setIsModalOpen(false)}
        bottomText={t(
          'giftCards.check.card.helperText',
          'Deine Kartennummer und den PIN-Code findest Du auf der Rückseite Deiner Geschenkkarte.',
        )}
        imageBack={
          <StaticCard
            image={{
              src: GIFT_CARD_BACK,
              alt: 'Gift-Karte',
            }}
          />
        }
        imageFront={
          <StaticCard
            image={{
              src: VIP_CARD,
              alt: 'VIP-Karte',
            }}
          />
        }
      />
      <GiftCardsBalanceWrapper>
        <GiftCardsBalanceContent>
          <Title>{t('giftCards.check.title', 'Kartensaldo abfragen')}</Title>
          <Description>
            {t(
              'giftCards.check.text',
              'Gib hier bitte die Kartennummer und den PIN-Code ein, um den Saldo deiner Geschenkkarte zu prüfen.',
            )}
          </Description>
          <HowToButton onClick={setIsModalOpen}>
            {t(
              'giftCards.check.linkCard',
              'Wo finde ich meine Kartennummer und den Pin-Code?',
            )}
          </HowToButton>
          <form onSubmit={handleSubmit} noValidate>
            <FormColumn>
              <StyledFormControl
                label={t('giftCards.check.form.card.label', 'Kartennummer')}
                hasError={touched.cardNr && !!errors.cardNr}
                message={
                  touched.cardNr &&
                  getErrorMessage(t, errors.cardNr, {
                    [REQUIRED_FIELD_ERROR]: {
                      defaultValue:
                        'Bitte gib die Nummer deiner Geschenkkarte ein.',
                      key: 'giftCards.check.cardNrRequiredError',
                    },
                    cardNumberLengthError: {
                      defaultValue: 'Die Nummer besteht aus genau 14 Zahlen.',
                      key: 'giftCards.check.cardNrLengthError',
                    },
                  })
                }
              >
                <TextInputMasked
                  name="cardNr"
                  value={values.cardNr}
                  mask={t(
                    'giftCards.check.form.card.placeholder',
                    '00 0000 0000 0000',
                  )}
                  blocks={blockOptions}
                  unmask
                  lazy={false}
                  overwrite
                  autofix
                  onChange={value => setFieldValue('cardNr', value)}
                  onBlur={handleBlur}
                  maskedInputType={MASKED_INPUT_TYPE.NUMBER}
                  novalidate
                  hasError={touched.cardNr && !!errors.cardNr}
                  disabled={isSubmitting}
                />
              </StyledFormControl>
              <StyledFormControl
                label={t('giftCards.check.form.pin.label', 'PIN-Code')}
                hasError={touched.pinCode && !!errors.pinCode}
                message={
                  touched.pinCode &&
                  getErrorMessage(t, errors.pinCode, {
                    [REQUIRED_FIELD_ERROR]: {
                      defaultValue: 'Bitte gib den Geschenkkarten Pin ein.',
                      key: 'giftCards.check.cardPinRequiredError',
                    },
                    pinCodeLengthError: {
                      defaultValue: 'Der PIN besteht aus genau 4 Zahlen.',
                      key: 'giftCards.check.cardPinLengthError',
                    },
                  })
                }
              >
                <TextInputMasked
                  name="pinCode"
                  value={values.pinCode}
                  mask={t('giftCards.check.form.pin.placeholder', '0000')}
                  blocks={blockOptions}
                  unmask
                  lazy={false}
                  overwrite
                  autofix
                  onChange={value => setFieldValue('pinCode', value)}
                  onBlur={handleBlur}
                  maskedInputType={MASKED_INPUT_TYPE.NUMBER}
                  novalidate
                  hasError={touched.pinCode && !!errors.pinCode}
                  disabled={isSubmitting}
                />
              </StyledFormControl>
            </FormColumn>
            <SubmitButton
              type="submit"
              disabled={isSubmitting}
              variant="primaryBig"
            >
              {t('giftCards.check.form.button', 'Abfragen')}
            </SubmitButton>
          </form>
        </GiftCardsBalanceContent>
      </GiftCardsBalanceWrapper>
    </React.Fragment>
  );
}

GiftCardBalance.validationSchema = Yup.object().shape({
  cardNr: Yup.string()
    .trim()
    .transform(value => value.replace(/ /g, ''))
    .length(14, 'cardNumberLengthError')
    .required(REQUIRED_FIELD_ERROR),
  pinCode: Yup.string()
    .trim()
    .transform(value => value.replace(/ /g, ''))
    .length(4, 'pinCodeLengthError')
    .required(REQUIRED_FIELD_ERROR),
});

export default GiftCardBalance;
